/**
 * Group Detail Modal
 * Modal que mostra informações detalhadas do grupo (assembly info, taxas, categorias)
 */

import { useEffect, useState } from 'react';
import { useNotify } from 'ra-core';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PageSpinner } from '@/components/ui/page-spinner';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Calendar, TrendingUp, Shield, Users, AlertCircle, Calculator, Hourglass, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { SimulationModal } from './SimulationModal';
import { textoClientesNorth, textoConsorciados } from './consorciados';
import { chancePorMes, textoUmEmCada } from './contemplaRapido';
import { SaleModal } from './SaleModal';
import { CotaSelectionModal } from './CotaSelectionModal';
import { parseVagasDisponiveis, isGrupoQuaseCheio, SEM_VAGAS_MSG, POUCAS_VAGAS_MSG } from './sale-options';
import {
  ASSEMBLEIAS_LANCE_TIPICO,
  contempladosPorAssembleiaRecente,
  lanceTipico,
  lanceVencedor,
  modalidadeDa,
  modalidadesDeLance,
  rotuloDaModalidade,
  type ModalidadeAssembleia,
} from './modalidadesAssembleia';

interface GroupDetails {
  id: string;
  group_number: number;
  contemplados_sorteio: number | null;
  contemplados_lance: number | null;
  lance_embutido: string | null;
  numero_consorciados: number | null;
  vencimento_dia: number | null;
  proxima_assembleia: string | null;
  fundo_reserva: string | null;
  taxa_admin: string | null;
  taxa_seguro: string | null;
  tipo_sorteio: string | null;
  tipo_seguro: string | null;
  tipo_grupo: string | null;
  previsao_reajuste: string | null;
  ordem_contemplacao: string | null;
  observacoes: string | null;
  raw_rules: string | null;
}

// AssemblyResultRow — vem da tabela servopa_assembly_results (uma por (grupo,
// assembleia)) + array de modalidades (SORTEIO / LANCE LIVRE / SEM EMBUTIDO /
// FIXO / FIDELIDADE)
// vindas de servopa_assembly_modalidades.
interface AssemblyModalidadeRow {
  modalidade: string;
  contemplados: number | null;
  menor_lance: number | null;
  quantidade_ofertas: number | null;
  percentual_concorrencia: number | null;
}

interface AssemblyResultRow {
  id: number;
  group_number: number;
  assembleia: number | null;
  data: string | null;
  cota_desempate: number | null;
  status: string | null;
  servopa_assembly_modalidades: AssemblyModalidadeRow[];
}

interface GroupCategory {
  id: string;
  group_number: number;
  admin_fee_pct: number;
  categoria_code: string;
  sequence: number;
  categoria_nome: string | null;
  valor_credito: string | null;
  prazo: number | null;
  percentual_mensal: string | null;
  parcela_sem_seguro: string | null;
  valor_seguro: string | null;
  total_parcela: string | null;
  observacao: string | null;
  purchase_url: string | null;
}

interface GroupDetailModalProps {
  groupNumber: number | null;
  open: boolean;
  onClose: () => void;
  /** Seção extra no fim do conteúdo, antes do rodapé — hoje usada pela
   *  página /consortium_groups/:groupNumber e pelo GrupoLink pra mostrar
   *  ativo/inativo e o histórico de disponibilidade. Opcional: a lista de
   *  Grupos e o ProposalPage não passam nada. */
  extraSection?: React.ReactNode;
  /** Aberto pela rota da página: o voltar do navegador já troca a rota. */
  daRota?: boolean;
}

export const GroupDetailModal = ({ groupNumber, open, onClose, extraSection, daRota = false }: GroupDetailModalProps) => {
  const [details, setDetails] = useState<GroupDetails | null>(null);
  // Clientes North com cota no grupo (migration 239). null = não deu para contar.
  const [clientesNorth, setClientesNorth] = useState<number | null>(null);
  const [categories, setCategories] = useState<GroupCategory[]>([]);
  const [assembleias, setAssembleias] = useState<AssemblyResultRow[]>([]);
  // `mod:<MODALIDADE>` ordena pelo lance vencedor daquela modalidade.
  type AssembliesSortKey = 'assembleia' | 'data' | 'sorteio' | 'qtd_ofertas' | `mod:${string}`;
  const [assembliesSort, setAssembliesSort] = useState<{ key: AssembliesSortKey; order: 'asc' | 'desc' }>({ key: 'assembleia', order: 'desc' });
  const [assembliesExpanded, setAssembliesExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [simulationOpen, setSimulationOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<GroupCategory | null>(null);
  const [saleOpen, setSaleOpen] = useState(false);
  const [cotaSelectionOpen, setCotaSelectionOpen] = useState(false);
  const [selectedCategoryForSale, setSelectedCategoryForSale] = useState<GroupCategory | null>(null);
  const [selectedCota, setSelectedCota] = useState<string | null>(null);
  const [loadingCotas, setLoadingCotas] = useState(false);
  const [segment, setSegment] = useState<'M' | 'I'>('M');
  const [selectedAdminFee, setSelectedAdminFee] = useState<number | null>(null);
  const [isFlex, setIsFlex] = useState(false);
  const [groupMonths, setGroupMonths] = useState<number | null>(null);
  const [groupAssembly, setGroupAssembly] = useState<number | null>(null);
  const [groupAtivo, setGroupAtivo] = useState(true);
  // null = vagas desconhecidas (dado ausente/mock) — nesse caso não travamos a venda
  const [groupVagas, setGroupVagas] = useState<number | null>(null);
  const [categoryDetailOpen, setCategoryDetailOpen] = useState<GroupCategory | null>(null);
  const [assemblyDetailOpen, setAssemblyDetailOpen] = useState<{
    assembleia: number;
    date: string | null;
    situacao: string;
    detail: AssemblyResultRow | null;
  } | null>(null);
  const isMobile = useIsMobile();
  const notify = useNotify();

  // Trava de vagas: com zero confirmado o clique em "Vender" avisa e não abre
  // nada — barrar aqui evita o vendedor montar a venda inteira pra falhar no fim.
  // Com <=10% das vagas restantes o fluxo abre normalmente, mas o vendedor é
  // avisado de que a venda sem seguro pode falhar.
  const handleVenderClick = (cat: GroupCategory) => {
    if (groupVagas === 0) {
      notify(SEM_VAGAS_MSG, { type: 'warning' });
      return;
    }
    if (isGrupoQuaseCheio(groupVagas, details?.numero_consorciados)) {
      notify(POUCAS_VAGAS_MSG, { type: 'warning' });
    }
    setSelectedCategoryForSale(cat);
    setLoadingCotas(true);
    setCotaSelectionOpen(true);
    // Reset loading after a short delay to show the modal
    setTimeout(() => setLoadingCotas(false), 300);
  };

  // Get unique admin fees from categories
  const adminFees = Array.from(new Set(categories.map(c => Number(c.admin_fee_pct)))).sort((a, b) => a - b);
  
  // Filter categories by selected admin fee
  const filteredCategories = selectedAdminFee !== null 
    ? categories
        .filter(c => Number(c.admin_fee_pct) === selectedAdminFee)
        .sort((a, b) => a.categoria_code.localeCompare(b.categoria_code))
    : categories;

  useEffect(() => {
    if (open && groupNumber) {
      loadGroupDetails(groupNumber);
    } else {
      // Reset state when modal closes
      setDetails(null);
      setCategories([]);
      setError(null);
      setSelectedAdminFee(null);
      setIsFlex(false);
    }
  }, [open, groupNumber]);

  // Set default admin fee when categories load
  useEffect(() => {
    if (categories.length > 0 && selectedAdminFee === null) {
      const fees = Array.from(new Set(categories.map(c => c.admin_fee_pct))).sort((a, b) => a - b);
      if (fees.length > 0) {
        setSelectedAdminFee(fees[0]);
      }
    }
  }, [categories, selectedAdminFee]);

  const loadGroupDetails = async (gn: number) => {
    setLoading(true);
    setError(null);
    setGroupVagas(null);
    setClientesNorth(null);

    try {
      // Mock data for group 9999 in dev
      if (import.meta.env.DEV && gn === 9999) {
        setSegment('I');
        setIsFlex(false);
        setDetails({
          id: 'mock-9999',
          group_number: 9999,
          contemplados_sorteio: 2,
          contemplados_lance: 1,
          lance_embutido: '0',
          numero_consorciados: 100,
          vencimento_dia: 15,
          proxima_assembleia: '2026-03-15',
          fundo_reserva: '2.50',
          taxa_admin: '18.00',
          taxa_seguro: '0.35',
          tipo_sorteio: 'SORTEIO NORMAL',
          tipo_seguro: 'OPCIONAL',
          tipo_grupo: 'NORMAL',
          previsao_reajuste: 'ANUAL',
          ordem_contemplacao: 'SORTEIO E LANCE',
          observacoes: 'Grupo de teste - DEV only',
          raw_rules: null
        });
        setCategories([{
          id: 'mock-cat-9999',
          group_number: 9999,
          admin_fee_pct: 18,
          categoria_code: 'A1',
          sequence: 1,
          categoria_nome: 'Categoria Test',
          valor_credito: '60000.00',
          prazo: 40,
          percentual_mensal: '2.82',
          parcela_sem_seguro: '1691.64',
          valor_seguro: '210.00',
          total_parcela: '1901.64',
          observacao: 'Mock category for testing',
          purchase_url: null,
        }]);
        setLoading(false);
        return;
      }

      const { data: groupData, error: groupError } = await supabase
        .from('servopa_groups_enriched')
        .select('segment, flex, months, assembly, ativo, vacancies')
        .eq('group_number', gn)
        .single();

      if (groupError) throw groupError;

      if (groupData?.segment) {
        setSegment(groupData.segment as 'M' | 'I');
      }

      setIsFlex(!!groupData?.flex);
      setGroupMonths(typeof groupData?.months === 'number' ? groupData.months : null);
      setGroupAssembly(typeof groupData?.assembly === 'number' ? groupData.assembly : null);
      setGroupAtivo(groupData?.ativo !== false);
      setGroupVagas(parseVagasDisponiveis(groupData?.vacancies));

      const { data: detailsData, error: detailsError } = await supabase
        .from('servopa_group_details')
        .select('*')
        .eq('group_number', gn)
        .maybeSingle();

      // Conta pela função (mesmo número para todo mundo, a RLS da venda não
      // recorta). Falhou → a linha some, sem inventar número.
      const { data: qtdClientes, error: clientesError } = await supabase.rpc(
        'clientes_north_no_grupo' as never,
        { p_group_number: gn } as never,
      );
      setClientesNorth(!clientesError && typeof qtdClientes === 'number' ? qtdClientes : null);

      if (detailsError) throw detailsError;

      setDetails({
        id: (detailsData as any)?.id || `group-${gn}`,
        group_number: (detailsData as any)?.group_number ?? gn,
        contemplados_sorteio: (detailsData as any)?.contemplados_sorteio ?? null,
        contemplados_lance: (detailsData as any)?.contemplados_lance ?? null,
        lance_embutido: (detailsData as any)?.lance_embutido ?? null,
        numero_consorciados: (detailsData as any)?.numero_consorciados ?? null,
        vencimento_dia: (detailsData as any)?.vencimento_dia ?? null,
        proxima_assembleia: (detailsData as any)?.proxima_assembleia ?? null,
        fundo_reserva: (detailsData as any)?.fundo_reserva ?? null,
        taxa_admin: (detailsData as any)?.taxa_admin ?? null,
        taxa_seguro: (detailsData as any)?.taxa_seguro ?? null,
        tipo_sorteio: (detailsData as any)?.tipo_sorteio ?? null,
        tipo_seguro: (detailsData as any)?.tipo_seguro ?? null,
        tipo_grupo: (detailsData as any)?.tipo_grupo ?? null,
        previsao_reajuste: (detailsData as any)?.previsao_reajuste ?? null,
        ordem_contemplacao: (detailsData as any)?.ordem_contemplacao ?? null,
        observacoes: (detailsData as any)?.observacoes ?? null,
        raw_rules: (detailsData as any)?.raw_rules ?? null,
      });

      // Carrega assembleias detalhadas — uma por linha, com modalidades aninhadas.
      // Fonte: servopa_assembly_results + servopa_assembly_modalidades, populadas
      // pelo scraper sync-assembly-details (modal "Sobre a Assembléia").
      const { data: assembleiasData, error: assembleiasError } = await supabase
        .from('servopa_assembly_results')
        .select('id, group_number, assembleia, data, cota_desempate, status, servopa_assembly_modalidades(modalidade, contemplados, menor_lance, quantidade_ofertas, percentual_concorrencia)')
        .eq('group_number', gn)
        .order('assembleia', { ascending: false });
      if (assembleiasError) {
        console.warn('Falha ao carregar histórico de assembleias:', assembleiasError.message);
        setAssembleias([]);
      } else {
        setAssembleias((assembleiasData ?? []) as unknown as AssemblyResultRow[]);
      }

      const { data: categoriesData, error: categoriesError } = await supabase
        .from('servopa_group_categories')
        .select('*')
        .eq('group_number', gn)
        .order('categoria_code')
        .order('sequence');

      if (categoriesError) throw categoriesError;
      setCategories((categoriesData || []) as any);
    } catch (err) {
      console.error('Error loading group details:', err);
      setError('Não foi possível carregar os detalhes do grupo.');
    } finally {
      setLoading(false);
    }
  };

  // raw_rules começa com as regras do grupo (ORDEM DE CONTEMPLACAO, lance,
  // seguro, reajuste) e a partir de "Contemplados por sorteio:" segue com
  // campos que já parseamos pra colunas (lance_embutido, fundo_reserva, etc.)
  // + tabela de categorias + lista de assembleias. Pra "Informações" só queremos
  // a parte regulamentar do começo.
  const extractRulesFromRawRules = (raw: string | null): string | null => {
    if (!raw) return null;
    const cutIdx = raw.search(/Contemplados\s+por\s+sorteio\s*:/i);
    let head = cutIdx >= 0 ? raw.slice(0, cutIdx) : raw;
    // Remove primeira linha lixo do tipo ": 1511" (número do grupo isolado)
    head = head.replace(/^:\s*\d+\s*\r?\n/, '');
    return head.trim() || null;
  };

  // Parseia a lista de assembleias do texto raw_rules (todas estão lá, mesmo
  // pra grupos sem entrada em servopa_assembly_results). Cada entrada do raw é tipo:
  //   "<num> <DD/MM/YYYY> - HH:MMh\nSERVOPA SA COMERCIO E INDUSTRIA\n  CURITIBA  REALIZADA  VISUALIZAR"
  const parseAssembliesFromRawRules = (raw: string | null): { assembleia: number; date: string; situacao: string }[] => {
    if (!raw) return [];
    const lines = raw.split(/\r?\n/);
    // Acha o início da seção "Assembléias"
    const startIdx = lines.findIndex(l => /^\s*Assembl[eé]ias?\s*$/i.test(l));
    const blockLines = startIdx >= 0 ? lines.slice(startIdx) : lines;
    const out: { assembleia: number; date: string; situacao: string }[] = [];
    const headerRe = /^\s*(\d{1,4})\s+(\d{2}\/\d{2}\/\d{4})\s*-\s*\d{2}:\d{2}h\s*$/;
    for (let i = 0; i < blockLines.length; i++) {
      const m = blockLines[i].match(headerRe);
      if (!m) continue;
      // Procurar situação nas próximas 3 linhas
      let situacao = '—';
      for (let j = 1; j <= 3 && i + j < blockLines.length; j++) {
        const tail = blockLines[i + j];
        const sm = tail.match(/(REALIZADA|A\s+REALIZAR|CANCELADA)/i);
        if (sm) { situacao = sm[1].toUpperCase().replace(/\s+/g, ' '); break; }
      }
      out.push({ assembleia: Number(m[1]), date: m[2], situacao });
    }
    return out;
  };

  const formatPercent = (value: number | string | null) => {
    if (value == null || value === '') return '—';
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    if (!Number.isFinite(n)) return '—';
    // Valores estão armazenados como % inteira (ex: 64 = 64%) ou fracionada (0.64).
    // Heurística: <= 1 = fração, > 1 = já em %.
    const pct = n > 1 ? n : n * 100;
    return `${pct.toFixed(0)}%`;
  };

  const formatCurrency = (value: string | number | null) => {
    if (!value) return '-';
    
    let num: number;
    if (typeof value === 'number') {
      num = value;
    } else if (typeof value === 'string') {
      // Remove tudo exceto dígitos e vírgula, depois converte vírgula para ponto
      const cleaned = value.replace(/[^\d,]/g, '').replace(',', '.');
      num = parseFloat(cleaned);
    } else {
      return '-';
    }
    
    if (isNaN(num)) return typeof value === 'string' ? value : '-';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(num);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()} voltarFecha={!daRota}>
      <DialogContent className="[&>button:last-child]:text-white [&>button:last-child]:data-[state=open]:bg-white/10 !max-w-[min(1400px,97vw)] w-[97vw] h-[94dvh] max-h-[94dvh] sm:h-[94vh] sm:max-h-[94vh] flex flex-col p-0 max-sm:!max-w-[100vw] max-sm:w-screen max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:rounded-none">
        <DialogHeader className="relative overflow-hidden bg-premium text-white px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 pr-12 sm:rounded-t-[inherit]">
          <DialogTitle className="flex items-center flex-wrap gap-2 text-lg sm:text-2xl font-mono text-white">
            Grupo {groupNumber}
            {!groupAtivo && (
              <span className="font-sans text-xs sm:text-sm bg-white/10 text-white/80 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full font-bold border border-white/25">
                INATIVO
              </span>
            )}
            {isFlex && (
              <span className="font-sans text-xs sm:text-sm bg-ouro text-marinho px-2 py-0.5 sm:px-3 sm:py-1 rounded-full font-bold">FLEX</span>
            )}
            {details?.raw_rules && /LANCE\s+FIDELIDADE\s+\d/i.test(details.raw_rules) && (
              <span className="font-sans text-xs sm:text-sm bg-white/10 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full font-bold border border-ouro/60">
                FIDELIDADE
              </span>
            )}
            {details?.tipo_grupo && (
              <Badge variant="outline" className="font-sans border-white/30 text-white">{details.tipo_grupo}</Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-white/72">
            Informações detalhadas do grupo de consórcio
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 space-y-6">
          {loading && <PageSpinner />}

          {error && (
            <div className="flex items-center gap-2 p-4 bg-destructive/10 text-destructive rounded">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && details && (
          <>
            {/* Aviso de vagas já na abertura do modal — mesmo critério do clique
                em "Vender" (handleVenderClick): zero confirmado trava a venda,
                quase cheio (<=10% do total) só alerta. Vagas desconhecidas ou
                total implausível não mostram nada. */}
            {groupVagas === 0 ? (
              <div className="flex items-center gap-2 p-4 bg-destructive/10 text-destructive rounded">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span>{SEM_VAGAS_MSG}</span>
              </div>
            ) : isGrupoQuaseCheio(groupVagas, details.numero_consorciados) ? (
              <div className="flex items-center gap-2 p-4 bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span>
                  {POUCAS_VAGAS_MSG} Restam {groupVagas} vagas das {details.numero_consorciados} cotas do grupo.
                </span>
              </div>
            ) : null}

            {/* Informações Gerais — 5 cards compactos numa linha em desktop */}
            {(() => {
              // Lance típico: mesma régua da lista de Grupos e da North IA
              // (migration 252) — lance vencedor das 3 últimas assembleias, um
              // por leilão livre (com e sem embutido são leilões separados).
              const tipico = lanceTipico(assembleias, 'LANCE LIVRE');
              const tipicoSem = lanceTipico(assembleias, 'SEM EMBUTIDO');
              // Ritmo de contemplação (o do selo "Contempla rápido" da lista):
              // chance de uma cota ser contemplada por mês.
              const chance = chancePorMes({
                contemplados_mes_recente: contempladosPorAssembleiaRecente(assembleias),
                cotas_grupo: details.numero_consorciados,
              });

              // Andamento usa a fonte canônica de servopa_groups (months total e
              // assembly decorridas). raw_rules às vezes lista só ~18 assembleias
              // (Servopa não renderiza tudo) e dava números errados tipo 10/18 num
              // grupo de 11/240.
              const list = parseAssembliesFromRawRules(details.raw_rules);
              const total = groupMonths ?? list.length;
              const decorridos = groupAssembly ?? list.filter(a => /REALIZADA|CANCELADA/i.test(a.situacao)).length;
              const restantes = total > 0 ? Math.max(0, total - decorridos) : 0;
              const pct = total > 0 ? Math.round((decorridos / total) * 100) : 0;

              return (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {details.proxima_assembleia && (
                    <div className="flex items-start gap-2 p-3 cartao border-t-ouro bg-card">
                      <Calendar className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-muted-foreground mb-0.5">Próxima assembleia</div>
                        <div className="text-sm font-semibold leading-tight">{details.proxima_assembleia}</div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-2 p-3 cartao border-t-ouro bg-card">
                    <Users className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-muted-foreground mb-0.5">Consorciados</div>
                      <div className="text-lg font-semibold">{textoConsorciados(details.numero_consorciados)}</div>
                      {clientesNorth !== null && (
                        <div className="text-xs text-muted-foreground">{textoClientesNorth(clientesNorth)}</div>
                      )}
                      {chance != null && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          contempla {textoUmEmCada(chance)} por mês{' '}
                          <span className="opacity-70">(últimas {ASSEMBLEIAS_LANCE_TIPICO} assembleias)</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {details.contemplados_sorteio !== null && (
                    <div className="flex items-start gap-2 p-3 cartao border-t-ouro bg-card">
                      <TrendingUp className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-muted-foreground mb-0.5">Contemplados por sorteio</div>
                        <div className="text-lg font-semibold">{details.contemplados_sorteio}</div>
                      </div>
                    </div>
                  )}

                  {details.contemplados_lance !== null && (
                    <div className="flex items-start gap-2 p-3 cartao border-t-ouro bg-card">
                      <TrendingUp className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-muted-foreground mb-0.5">Contemplados por lance</div>
                        <div className="text-lg font-semibold">{details.contemplados_lance}</div>
                        {(tipico.media != null || tipicoSem.media != null) && (
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            lance típico
                            {tipico.media != null && <> · com embutido {tipico.media.toFixed(1)}%</>}
                            {tipicoSem.media != null && <> · sem embutido {tipicoSem.media.toFixed(1)}%</>}{' '}
                            <span className="opacity-70">(últimas {ASSEMBLEIAS_LANCE_TIPICO} assembleias)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {total > 0 && (
                    <div className="flex items-start gap-2 p-3 cartao border-t-ouro bg-card">
                      <Hourglass className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-muted-foreground mb-0.5">Andamento</div>
                        <div className="text-lg font-semibold leading-tight">{decorridos}/{total}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          <span className="text-amber-700 dark:text-amber-400">{restantes} restantes</span>
                        </div>
                        <div className="mt-1.5 h-1.5 w-full bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-marinho transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Taxas e Configurações */}
            <div className="cartao p-5 sm:p-6 bg-card">
              <h3 className="font-semibold text-lg mb-6 flex items-center gap-2 text-marinho">
                <Shield className="h-5 w-5" />
                Taxas e configurações
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                {details.taxa_admin && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Taxa de administração</span>
                    <span className="font-semibold">{details.taxa_admin}</span>
                  </div>
                )}
                {details.taxa_seguro && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Taxa de seguro</span>
                    <span className="font-semibold">{details.taxa_seguro}</span>
                  </div>
                )}
                {details.fundo_reserva && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Fundo de reserva</span>
                    <span className="font-semibold">{details.fundo_reserva}</span>
                  </div>
                )}
                {details.vencimento_dia && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Dia de vencimento</span>
                    <span className="font-semibold">Dia {details.vencimento_dia}</span>
                  </div>
                )}
                {details.tipo_sorteio && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Tipo de sorteio</span>
                    <span className="font-semibold">{details.tipo_sorteio}</span>
                  </div>
                )}
                {details.tipo_seguro && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Tipo de seguro</span>
                    <span className="font-semibold">{details.tipo_seguro}</span>
                  </div>
                )}
                {details.lance_embutido && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Lance embutido</span>
                    <span className="font-semibold">{details.lance_embutido}</span>
                  </div>
                )}
                {details.ordem_contemplacao && (
                  <div className="md:col-span-3">
                    <span className="text-muted-foreground block mb-1">Ordem de contemplação</span>
                    <div className="font-semibold whitespace-pre-line">
                      {details.ordem_contemplacao.replace(/\.\s*-/g, '.\n-').replace(/\.\s+([A-Z])/g, '.\n$1')}
                    </div>
                  </div>
                )}
                {details.previsao_reajuste && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Previsão de reajuste</span>
                    <span className="font-semibold">{details.previsao_reajuste}</span>
                  </div>
                )}
              </div>

              {details.observacoes && (
                <div className="mt-4 pt-4 border-t">
                  <span className="text-muted-foreground block mb-1">Observações</span>
                  <p className="text-sm">{details.observacoes}</p>
                </div>
              )}
            </div>

            {/* Categorias (Planos de Crédito) */}
            {categories.length > 0 && (
              <div className="cartao p-4 sm:p-6 bg-card">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <h3 className="font-semibold text-lg text-marinho">
                    Categorias disponíveis ({filteredCategories.length})
                  </h3>
                  {/* Admin Fee Tabs */}
                  {adminFees.length > 1 && (
                    <div className="flex flex-wrap gap-2">
                      {adminFees.map(fee => (
                        <button
                          key={fee}
                          onClick={() => setSelectedAdminFee(fee)}
                          className={`cursor-pointer px-3.5 py-1.5 rounded-[7px] text-sm font-medium transition-colors ${
                            selectedAdminFee === fee
                              ? 'bg-marinho text-white'
                              : 'bg-muted hover:bg-muted/80'
                          }`}
                        >
                          Taxa ADM {fee}%
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {isMobile ? (
                  <div className="flex flex-col gap-3" key={`cards-${selectedAdminFee}`}>
                    {filteredCategories.map((cat) => (
                      <div key={`m-${selectedAdminFee}-${cat.id}`} className="cartao p-3 bg-card">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="font-semibold text-base truncate">
                            {cat.categoria_code}
                            {cat.categoria_nome ? <span className="text-muted-foreground font-normal"> · {cat.categoria_nome}</span> : null}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-xs text-muted-foreground">Crédito</div>
                            <div className="font-semibold">{formatCurrency(cat.valor_credito)}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                          <div><span className="text-muted-foreground">Prazo:</span> <span className="font-medium">{cat.prazo ? `${cat.prazo}m` : '-'}</span></div>
                          <div className="text-right"><span className="text-muted-foreground">Parcela total:</span> <span className="font-semibold text-[#0E7A3E]">{formatCurrency(cat.total_parcela)}</span></div>
                        </div>
                        <div className="mt-3 flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="flex-1 min-w-0"
                            onClick={(e) => { e.stopPropagation(); setCategoryDetailOpen(cat); }}
                          >
                            Detalhes
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 min-w-0"
                            onClick={(e) => { e.stopPropagation(); setSelectedCategory(cat); setSimulationOpen(true); }}
                          >
                            <Calculator className="h-4 w-4 mr-1" />
                            Simular
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 min-w-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVenderClick(cat);
                            }}
                          >
                            Vender
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="w-full">
                    <Table key={`table-${selectedAdminFee}`}>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Código · nome</TableHead>
                          <TableHead className="text-right">Crédito</TableHead>
                          <TableHead className="text-center">Prazo</TableHead>
                          <TableHead className="text-right">% Mensal</TableHead>
                          <TableHead className="text-right">Parcela s/ Seguro · seguro</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-center">Ação</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody key={`tbody-${selectedAdminFee}`}>
                        {filteredCategories.map((cat) => (
                          <TableRow key={`${selectedAdminFee}-${cat.id}`}>
                            <TableCell className="whitespace-normal">
                              <div className="font-mono font-semibold">{cat.categoria_code}</div>
                              <div className="text-xs text-muted-foreground">{cat.categoria_nome || '-'}</div>
                            </TableCell>
                            <TableCell className="text-right font-semibold tabular-nums">{formatCurrency(cat.valor_credito)}</TableCell>
                            <TableCell className="text-center tabular-nums">{cat.prazo ? `${cat.prazo}m` : '-'}</TableCell>
                            <TableCell className="text-right tabular-nums">{cat.percentual_mensal || '-'}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              <div>{formatCurrency(cat.parcela_sem_seguro)}</div>
                              <div className="text-xs text-muted-foreground">seguro {formatCurrency(cat.valor_seguro)}</div>
                            </TableCell>
                            <TableCell className="text-right font-semibold tabular-nums text-[#0E7A3E]">
                              {formatCurrency(cat.total_parcela)}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-wrap gap-1.5 justify-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCategory(cat);
                                    setSimulationOpen(true);
                                  }}
                                >
                                  <Calculator className="h-4 w-4 mr-1" />
                                  Simular
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleVenderClick(cat);
                                  }}
                                >
                                  Vender
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {/* Lista de Assembleias — lista do raw_rules + dados ricos de
                servopa_assembly_results / servopa_assembly_modalidades quando há.
                1 linha por assembleia: sorteio e, em cada modalidade de lance
                que o grupo tem, contemplados · menor lance vencedor. */}
            {(() => {
              const rawList = parseAssembliesFromRawRules(details.raw_rules);
              const detailsByNum = new Map<number, AssemblyResultRow>();
              for (const a of assembleias) if (a.assembleia != null) detailsByNum.set(a.assembleia, a);

              type Row = {
                assembleia: number;
                date: string | null;
                situacao: string;
                detail: AssemblyResultRow | null;
              };
              const rows: Row[] = rawList.map(r => ({
                assembleia: r.assembleia,
                date: r.date,
                situacao: r.situacao,
                detail: detailsByNum.get(r.assembleia) ?? null,
              }));
              const seen = new Set(rows.map(r => r.assembleia));
              for (const a of assembleias) {
                if (a.assembleia != null && !seen.has(a.assembleia)) {
                  rows.push({ assembleia: a.assembleia, date: a.data, situacao: '—', detail: a });
                }
              }

              const findMod = (d: AssemblyResultRow | null, name: string) => modalidadeDa(d, name);
              const modalidades = modalidadesDeLance(assembleias);
              const celulaModalidade = (m: ModalidadeAssembleia | null) => {
                if (!m) return '—';
                const venc = lanceVencedor(m);
                return venc != null ? `${m.contemplados ?? 0} · ${formatPercent(venc)}` : String(m.contemplados ?? 0);
              };

              // Ordena com base no estado assembliesSort
              const sortKey = assembliesSort.key;
              const sortOrder = assembliesSort.order;
              const valueOf = (r: typeof rows[number]): number | string => {
                const sorteio = findMod(r.detail, 'SORTEIO');
                const livre = findMod(r.detail, 'LANCE LIVRE');
                if (sortKey.startsWith('mod:')) return lanceVencedor(findMod(r.detail, sortKey.slice(4))) ?? -1;
                switch (sortKey) {
                  case 'assembleia': return r.assembleia;
                  case 'data': {
                    if (!r.date) return '';
                    // raw_rules dá DD/MM/YYYY; converte pra YYYY-MM-DD pra ordenar string corretamente
                    const m = r.date.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                    return m ? `${m[3]}-${m[2]}-${m[1]}` : r.date;
                  }
                  case 'sorteio': return sorteio?.contemplados ?? -1;
                  case 'qtd_ofertas': return livre?.quantidade_ofertas ?? -1;
                  default: return -1;
                }
              };
              const sortedRows = [...rows].sort((a, b) => {
                const va = valueOf(a);
                const vb = valueOf(b);
                let cmp = 0;
                if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
                else cmp = String(va).localeCompare(String(vb));
                return sortOrder === 'asc' ? cmp : -cmp;
              });

              // Modo colapsado: só as 5 últimas REALIZADA (mais recentes pela
              // ordenação default por assembleia desc; respeitamos a ordenação
              // ativa pra UX consistente).
              const COLLAPSED_LIMIT = 5;
              const realizadasRows = sortedRows.filter(r => r.situacao === 'REALIZADA');
              const isCollapsible = realizadasRows.length > COLLAPSED_LIMIT;
              const visibleRows = !assembliesExpanded && isCollapsible
                ? realizadasRows.slice(0, COLLAPSED_LIMIT)
                : sortedRows;
              const hiddenCount = sortedRows.length - visibleRows.length;

              const SortHeader = ({ label, k, align }: { label: string; k: AssembliesSortKey; align?: 'left' | 'center' | 'right' }) => {
                const active = sortKey === k;
                const Arrow = active ? (sortOrder === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
                return (
                  <TableHead className={`cursor-pointer select-none hover:bg-muted/40 ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''}`}
                             onClick={() => setAssembliesSort(prev => ({
                               key: k,
                               order: prev.key === k ? (prev.order === 'asc' ? 'desc' : 'asc') : 'desc',
                             }))}>
                    <div className={`inline-flex items-center gap-1 ${justify}`}>
                      <span>{label}</span>
                      <Arrow className={`h-3 w-3 ${active ? '' : 'opacity-40'}`} />
                    </div>
                  </TableHead>
                );
              };

              return (
                <div className="cartao p-5 sm:p-6 bg-card">
                  <h3 className="font-semibold text-lg mb-4 text-marinho">
                    Lista de assembleias{rows.length > 0 ? ` (${rows.length})` : ''}
                  </h3>
                  {modalidades.length > 0 && (
                    <p className="-mt-2 mb-3 text-xs text-muted-foreground">
                      Em cada modalidade: contemplados · menor lance vencedor. A Servopa não publica o maior lance.
                    </p>
                  )}
                  {rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sem dados de assembleias para este grupo.</p>
                  ) : isMobile ? (
                    <div className="flex flex-col gap-2">
                      {visibleRows.map((r) => {
                        const sorteio = findMod(r.detail, 'SORTEIO');
                        return (
                          <button
                            key={r.assembleia}
                            type="button"
                            onClick={() => setAssemblyDetailOpen(r)}
                            className="text-left cartao p-3 bg-card hover:bg-muted/30 transition-colors cursor-pointer"
                          >
                            <div className="flex items-baseline justify-between gap-2">
                              <div className="font-semibold">Assembleia {r.assembleia}</div>
                              <div className="text-xs text-muted-foreground">{r.date ?? '—'}</div>
                            </div>
                            <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                              <span className={
                                r.situacao === 'REALIZADA'
                                  ? 'text-green-700 dark:text-green-400'
                                  : r.situacao === 'A REALIZAR'
                                    ? 'text-amber-700 dark:text-amber-400'
                                    : r.situacao === 'CANCELADA'
                                      ? 'text-red-700 dark:text-red-400'
                                      : 'text-muted-foreground'
                              }>{r.situacao}</span>
                              {sorteio?.contemplados != null && (
                                <span className="text-muted-foreground">· Sorteio {sorteio.contemplados}</span>
                              )}
                              {modalidades.map((nome) => {
                                const m = findMod(r.detail, nome);
                                return m ? (
                                  <span key={nome} className="text-muted-foreground">
                                    · {rotuloDaModalidade(nome)} {celulaModalidade(m)}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="w-full overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <SortHeader label="Assembléia" k="assembleia" align="center" />
                            <SortHeader label="Data" k="data" align="center" />
                            <TableHead>Situação</TableHead>
                            <SortHeader label="Sorteio" k="sorteio" align="center" />
                            {modalidades.map((nome) => (
                              <SortHeader key={nome} label={rotuloDaModalidade(nome)} k={`mod:${nome}`} align="center" />
                            ))}
                            <SortHeader label="Ofertas (livre)" k="qtd_ofertas" align="right" />
                            <TableHead className="text-right">% Concorr. (livre)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visibleRows.map((r) => {
                            const sorteio = findMod(r.detail, 'SORTEIO');
                            const livre = findMod(r.detail, 'LANCE LIVRE');
                            return (
                              <TableRow key={r.assembleia}>
                                <TableCell className="text-center font-medium">{r.assembleia}</TableCell>
                                <TableCell className="text-center">{r.date ?? '—'}</TableCell>
                                <TableCell>
                                  <span className={
                                    r.situacao === 'REALIZADA'
                                      ? 'text-green-700 dark:text-green-400'
                                      : r.situacao === 'A REALIZAR'
                                        ? 'text-amber-700 dark:text-amber-400'
                                        : r.situacao === 'CANCELADA'
                                          ? 'text-red-700 dark:text-red-400'
                                          : ''
                                  }>{r.situacao}</span>
                                </TableCell>
                                <TableCell className="text-center">{sorteio?.contemplados ?? '—'}</TableCell>
                                {modalidades.map((nome) => (
                                  <TableCell key={nome} className="text-center whitespace-nowrap">
                                    {celulaModalidade(findMod(r.detail, nome))}
                                  </TableCell>
                                ))}
                                <TableCell className="text-right">{livre?.quantidade_ofertas ?? '—'}</TableCell>
                                <TableCell className="text-right">{livre ? formatPercent(livre.percentual_concorrencia) : '—'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  {isCollapsible && (
                    <div className="mt-3 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setAssembliesExpanded(v => !v)}
                        className="text-sm text-primary hover:underline"
                      >
                        {assembliesExpanded
                          ? `Mostrar menos (5 mais recentes)`
                          : `Mostrar mais (+${hiddenCount} assembleias)`}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Informações — só a parte regulamentar do raw_rules (até antes
                de "Contemplados por sorteio:"). O restante já vira coluna
                em outras seções da UI. */}
            {(() => {
              const info = extractRulesFromRawRules(details.raw_rules);
              if (!info) return null;
              return (
                <div className="cartao p-5 sm:p-6 bg-card">
                  <h3 className="font-semibold text-lg mb-4 text-marinho">Informações</h3>
                  <pre className="p-4 bg-muted rounded text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-[60vh] overflow-y-auto">
                    {info}
                  </pre>
                </div>
              );
            })()}

            {extraSection}
          </>
        )}
        </div>

        <DialogFooter className="px-4 sm:px-6 py-3 sm:py-4 border-t shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Button
            onClick={onClose}
            className="w-full sm:w-auto bg-[#354660] text-white hover:bg-[#29374B] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Mobile-only: Category detail */}
      <Dialog open={!!categoryDetailOpen} onOpenChange={(o) => !o && setCategoryDetailOpen(null)}>
        <DialogContent className="max-w-[calc(100%-1.5rem)] sm:max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {categoryDetailOpen?.categoria_code} {categoryDetailOpen?.categoria_nome ? `· ${categoryDetailOpen.categoria_nome}` : ''}
            </DialogTitle>
            <DialogDescription>Detalhes da categoria</DialogDescription>
          </DialogHeader>
          {categoryDetailOpen && (
            <div className="text-sm space-y-2">
              <DetailRow label="Crédito" value={formatCurrency(categoryDetailOpen.valor_credito)} highlight />
              <DetailRow label="Prazo" value={categoryDetailOpen.prazo ? `${categoryDetailOpen.prazo} meses` : '-'} />
              <DetailRow label="% Mensal" value={categoryDetailOpen.percentual_mensal || '-'} />
              <DetailRow label="Parcela s/ Seguro" value={formatCurrency(categoryDetailOpen.parcela_sem_seguro)} />
              <DetailRow label="Seguro" value={formatCurrency(categoryDetailOpen.valor_seguro)} />
              <DetailRow label="Total da parcela" value={formatCurrency(categoryDetailOpen.total_parcela)} highlight />
            </div>
          )}
          <DialogFooter className="pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            <Button variant="outline" className="w-full" onClick={() => setCategoryDetailOpen(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile-only: Assembly detail */}
      <Dialog open={!!assemblyDetailOpen} onOpenChange={(o) => !o && setAssemblyDetailOpen(null)}>
        <DialogContent className="max-w-[calc(100%-1.5rem)] sm:max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Assembleia {assemblyDetailOpen?.assembleia}
            </DialogTitle>
            <DialogDescription>{assemblyDetailOpen?.date ?? '—'} · {assemblyDetailOpen?.situacao}</DialogDescription>
          </DialogHeader>
          {assemblyDetailOpen && (() => {
            const findMod = (name: string) => modalidadeDa(assemblyDetailOpen.detail, name);
            const sorteio = findMod('SORTEIO');
            const livre = findMod('LANCE LIVRE');
            return (
              <div className="text-sm space-y-2">
                <DetailRow label="Sorteio (contemplados)" value={sorteio?.contemplados ?? '—'} />
                {modalidadesDeLance(assembleias).map((nome) => {
                  const m = findMod(nome);
                  if (!m) return null;
                  const venc = lanceVencedor(m);
                  return (
                    <DetailRow
                      key={nome}
                      label={`${rotuloDaModalidade(nome)} (contemplados · menor lance)`}
                      value={venc != null ? `${m.contemplados ?? 0} · ${formatPercent(venc)}` : (m.contemplados ?? 0)}
                    />
                  );
                })}
                <DetailRow label="Qtd. Ofertas (livre)" value={livre?.quantidade_ofertas ?? '—'} />
                <DetailRow label="% Concorrência (livre)" value={livre ? formatPercent(livre.percentual_concorrencia) : '—'} />
              </div>
            );
          })()}
          <DialogFooter className="pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            <Button variant="outline" className="w-full" onClick={() => setAssemblyDetailOpen(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Simulation Modal */}
      <SimulationModal
        open={simulationOpen}
        onClose={() => setSimulationOpen(false)}
        groupNumber={groupNumber || 0}
        category={selectedCategory}
        isFlex={isFlex}
        segment={segment}
      />

      {/* Cota Selection Modal */}
      <CotaSelectionModal
        open={cotaSelectionOpen}
        onClose={() => {
          setCotaSelectionOpen(false);
          setSelectedCategoryForSale(null);
          setSelectedCota(null);
        }}
        onContinue={(cota) => {
          setSelectedCota(cota);
          setCotaSelectionOpen(false);
          setSaleOpen(true);
        }}
        groupNumber={groupNumber || 0}
        category={selectedCategoryForSale}
        adminFee={selectedAdminFee}
        loading={loadingCotas}
      />

      {/* Sale Modal */}
      <SaleModal
        open={saleOpen}
        onClose={() => {
          setSaleOpen(false);
          setSelectedCategoryForSale(null);
          setSelectedCota(null);
        }}
        groupNumber={groupNumber || 0}
        category={selectedCategoryForSale}
        segment={segment}
        adminFee={selectedAdminFee}
        selectedCota={selectedCota}
        isFlex={isFlex}
      />
    </Dialog>
  );
};

const DetailRow = ({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b last:border-b-0">
    <span className="text-muted-foreground">{label}</span>
    <span className={highlight ? 'font-semibold text-green-700 dark:text-green-400' : 'font-medium'}>
      {value}
    </span>
  </div>
);
